import type { SelectChangeEvent } from '@mui/material'
import { Box, MenuItem, Select, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import type { MouseEvent } from 'react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type { MarketAvailability } from '@/features/bidding/hooks/useForecastMarketAvailability'
import type {
  ForecastMarketPair,
  LevelPricingConfig,
  PriceQuantile,
  PricingMode,
} from '@/features/bidding/types/forecastPricing'
import { MARKET_PAIR_OPTIONS, QUANTILE_OPTIONS, QUANTILE_ORDER } from '@/features/bidding/types/forecastPricing'

type LevelPricingCardProps = {
  levelIndex: number
  config: LevelPricingConfig
  minQuantile: PriceQuantile
  isFocused: boolean
  isForecastAvailable: boolean
  marketAvailability: Map<ForecastMarketPair, MarketAvailability>
  onModeChange: (mode: PricingMode) => void
  onMarketChange: (market: ForecastMarketPair) => void
  onQuantileChange: (quantile: PriceQuantile) => void
  onFocus: () => void
}

function LevelPricingCard({
  levelIndex,
  config,
  minQuantile,
  isFocused,
  isForecastAvailable,
  marketAvailability,
  onModeChange,
  onMarketChange,
  onQuantileChange,
  onFocus,
}: Readonly<LevelPricingCardProps>) {
  const { t } = useTranslation()
  const levelNumber = levelIndex + 1

  const handleModeChange = useCallback(
    (_event: MouseEvent<HTMLElement>, newMode: PricingMode | null) => {
      if (newMode !== null) {
        onModeChange(newMode)
      }
    },
    [onModeChange],
  )

  const handleMarketChange = useCallback(
    (event: SelectChangeEvent<ForecastMarketPair>) => {
      onMarketChange(event.target.value as ForecastMarketPair)
    },
    [onMarketChange],
  )

  const handleQuantileChange = useCallback(
    (event: SelectChangeEvent<PriceQuantile>) => {
      onQuantileChange(event.target.value as PriceQuantile)
    },
    [onQuantileChange],
  )

  const handleCardClick = useCallback(() => {
    onFocus()
  }, [onFocus])

  const minQuantileIndex = QUANTILE_ORDER.indexOf(minQuantile)

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: isFocused ? 'primary.main' : 'divider',
        borderRadius: 1,
        p: 1,
        backgroundColor: isFocused ? 'action.hover' : 'background.paper',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        cursor: 'pointer',
      }}
      onClick={handleCardClick}
    >
      {/* Header row: Level title + Mode toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {t('bidding.bidding_cascading.configuration.level_title', { level: levelNumber })}
        </Typography>

        <ToggleButtonGroup
          exclusive
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              py: 0.25,
              px: 1,
              fontSize: '0.6875rem',
              textTransform: 'none',
            },
          }}
          value={config.mode}
          onChange={handleModeChange}
        >
          <ToggleButton value="fixed">{t('bidding.bidding_cascading.configuration.mode_fixed')}</ToggleButton>
          {isForecastAvailable ? (
            <ToggleButton value="forecast">{t('bidding.bidding_cascading.configuration.mode_forecast')}</ToggleButton>
          ) : (
            <Tooltip placement="top" title={t('bidding.bidding_cascading.configuration.no_forecast_data')}>
              <span>
                <ToggleButton disabled value="forecast">
                  {t('bidding.bidding_cascading.configuration.mode_forecast')}
                </ToggleButton>
              </span>
            </Tooltip>
          )}
        </ToggleButtonGroup>
      </Box>

      {/* Forecast Settings (only shown in forecast mode) */}
      {config.mode === 'forecast' && (
        <Box
          sx={{ display: 'flex', gap: 1, mt: 1 }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Market Select */}
          <Select
            MenuProps={{
              // Ensure menu renders in portal outside contained sidebar
              container: document.body,
              // Prevent scroll lock which interferes with CSS containment
              disableScrollLock: true,
              // Ensure clean unmount to avoid ghost backdrops
              keepMounted: false,
            }}
            size="small"
            sx={{
              flex: 1,
              fontSize: '0.75rem',
              '& .MuiSelect-select': { py: 0.5 },
            }}
            value={config.market}
            onChange={handleMarketChange}
          >
            {MARKET_PAIR_OPTIONS.map((option) => {
              const availability = marketAvailability.get(option.value)
              const isUnavailable = availability && !availability.hasData
              const isIncomplete = availability && availability.hasData && !availability.isComplete
              const unavailableReason = isUnavailable
                ? t('bidding.bidding_cascading.configuration.market_unavailable')
                : undefined

              const menuItem = (
                <MenuItem
                  key={option.value}
                  aria-label={unavailableReason ? `${option.label} - ${unavailableReason}` : undefined}
                  disabled={isUnavailable}
                  sx={{
                    fontSize: '0.75rem',
                    color: isIncomplete ? 'warning.main' : undefined,
                  }}
                  value={option.value}
                >
                  {option.label}
                  {isIncomplete && availability && (
                    <Typography component="span" sx={{ fontSize: '0.625rem', ml: 0.5, color: 'warning.main' }}>
                      ({availability.availableHours}/{availability.expectedHours}h)
                    </Typography>
                  )}
                </MenuItem>
              )

              if (isUnavailable) {
                return (
                  <Tooltip key={option.value} placement="right" title={unavailableReason}>
                    <span>{menuItem}</span>
                  </Tooltip>
                )
              }

              return menuItem
            })}
          </Select>

          {/* Quantile Select */}
          <Select
            MenuProps={{
              // Ensure menu renders in portal outside contained sidebar
              container: document.body,
              // Prevent scroll lock which interferes with CSS containment
              disableScrollLock: true,
              // Ensure clean unmount to avoid ghost backdrops
              keepMounted: false,
            }}
            size="small"
            sx={{
              minWidth: 80,
              fontSize: '0.75rem',
              '& .MuiSelect-select': { py: 0.5 },
            }}
            value={config.quantile}
            onChange={handleQuantileChange}
          >
            {QUANTILE_OPTIONS.map((option, index) => {
              const isDisabled = index < minQuantileIndex
              return (
                <MenuItem key={option.value} disabled={isDisabled} sx={{ fontSize: '0.75rem' }} value={option.value}>
                  {option.label}
                </MenuItem>
              )
            })}
          </Select>
        </Box>
      )}

      {/* Fixed Mode Info */}
      {config.mode === 'fixed' && (
        <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', mt: 0.5 }}>
          {t('bidding.bidding_cascading.configuration.fixed_hint')}
        </Typography>
      )}
    </Box>
  )
}

export default LevelPricingCard
